/**
 * Shared time constants for `apps/web`.
 *
 * PATTERN TO FOLLOW: any cache, debounce, or timeout duration used
 * in more than one place (or representing a product decision, not a local
 * detail) lives here, not as a loose `const` inside a component.
 *
 * Reason: these values were redeclared in ~12 files with two spellings
 * of the same number (`5 * 60 * 1000` and `300_000`), so changing
 * the cache policy meant touching all those places and risking divergent
 * values. Import from here instead of redefining.
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;

/** Milliseconds in a minute. Base for deriving the remaining durations. */
const MINUTE_MS = MS_PER_SECOND * SECONDS_PER_MINUTE;

/** Milliseconds in an hour. Used by the foreground reminder window. */
export const ONE_HOUR_MS = MINUTES_PER_HOUR * MINUTE_MS;

/** Minutes a cached datum is considered fresh (offline and session cache). */
const STALE_MINUTES = 5;

/**
 * Default `staleTime` for offline-first queries (PowerSync + TanStack).
 *
 * Data is synchronized locally via PowerSync, so a generous `staleTime`
 * (5 min) avoids redundant refetches/re-renders without a real risk of
 * showing stale data. It is also the global `queryClient` default.
 */
export const OFFLINE_CACHE_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * `staleTime` for session/authentication queries.
 *
 * It remains a separate constant (although today it matches the offline
 * cache value) because it is an independent decision: it must stay aligned
 * between `useSession`, the `_app` route guard, and the `session-bridge`.
 */
export const SESSION_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * Time ceiling for the session check of route guards (`beforeLoad`).
 *
 * P23 / CC-60 · I1-bis: it lived as a LOCAL literal inside `runSessionCheck`
 * (`_app/route.tsx`), so the defense existed and worked… but **never
 * reached its sibling guard `_auth/route.tsx`**, which awaited it without a
 * ceiling. With `get-session` STALLED (held: neither 401 nor 5xx, which resolve
 * quickly) `_auth`'s `beforeLoad` did not resolve, the router did not mount the
 * route, and `/login` stayed with EMPTY `document.body.innerText` at
 * 3/6/10/15/20 s — measured in this repo on the dev server **and** in the
 * production bundle, while `/` painted normally (that is why check I1 came out
 * green: it measured the route that was healthy).
 *
 * That is why it lives HERE and not in either guard: it is a shared product
 * decision, and the one that lacks it stays blank.
 */
export const SESSION_CHECK_TIMEOUT_MS = 1500;

/**
 * Debounce for text inputs that persist while typing (detail title and notes,
 * subtasks). Balances typing smoothness against the number of mutations.
 */
export const DEBOUNCE_DELAY_MS = 400;

/** Seconds before revoking a download's object URL (see constant). */
const OBJECT_URL_REVOKE_SECONDS = 10;

/**
 * Grace period before revoking a `blob:` object URL after starting a download.
 *
 * It must comfortably exceed the time the browser takes to consume the href
 * of the `<a download>`; revoking earlier would abort the download.
 */
export const OBJECT_URL_REVOKE_DELAY_MS = OBJECT_URL_REVOKE_SECONDS * MS_PER_SECOND;
